import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { IconTooltip } from 'sonata-react-components';
import { formatNumber, formatPercentage } from 'sonata-static-storage/lib/helpers/generalFormatters.helpers';
import { normalizeWithUnitAndThousandAndMillions } from 'sonata-static-storage/lib/helpers/i18nUnits.helpers';

import { useGlobalStore } from '@/store/global';
import { getBaseCurrency } from '@/store/global/baseCurrency/selectors';
import { formatMoney } from '../helpers/formatters';
import { PrimaryKpiCard } from './PrimaryKpiCard/PrimaryKpiCard';
import { SecondaryKpiCard } from './SecondaryKpiCard/SecondaryKpiCard';

import type { KpiOverview as KpiOverviewType } from 'sonata-static-storage/lib/entities/footfallReport/footfallReport.types';

import './kpiOverview.scss';

type Props = {
  data: KpiOverviewType;
  isAggregated?: boolean;
  primaryOnly?: boolean;
  secondaryOnly?: boolean;
};

export default function KpiOverview({ data, isAggregated = false, primaryOnly = false, secondaryOnly = false }: Props) {
  const { formatMessage } = useIntl();
  const baseCurrency = useGlobalStore(getBaseCurrency);
  const formatCountry = baseCurrency?.countries?.[0] || 'default';

  const formatNumberValue = (value: number | null | undefined): string => {
    if (value == null) {
      return '—';
    }
    return formatNumber(value, { country: formatCountry, fixed: 0 });
  };

  const formatNormalizedCount = (value: number | null | undefined): string => {
    if (value == null) {
      return '—';
    }
    return normalizeWithUnitAndThousandAndMillions(value, {
      countries: baseCurrency?.countries,
      currencySymbol: ''
    });
  };

  const formatPercent = (value: number | null | undefined, base: 1 | 100 = 100, sign = false): string => {
    if (value == null) {
      return '—';
    }
    const formatted = formatPercentage(value, {
      base,
      country: formatCountry,
      fixedDecimals: 2
    });
    return sign && value > 0 ? `+${formatted}` : formatted;
  };

  const formatCurrencyRange = (range: { min: number; max: number } | null | undefined): string => {
    if (range == null) {
      return '—';
    }
    const symbol = baseCurrency?.currencySymbol ?? '';
    const isLeft = baseCurrency?.currencyPosition === 'left';

    const min = formatNumber(range.min, { country: formatCountry, fixed: 0 });
    const max = formatNumber(range.max, { country: formatCountry, fixed: 0 });
    const rangeStr = `${min}-${max}`;
    return isLeft ? `${symbol}${rangeStr}` : `${rangeStr}${symbol}`;
  };

  const formatPercentRange = (range: { min: number; max: number } | null | undefined, base: 1 | 100 = 100): string => {
    if (range == null) {
      return '—';
    }
    const factor = base === 1 ? 100 : 1;
    return `${formatNumber(range.min * factor, { fixed: 0 })}-${formatNumber(range.max * factor, { fixed: 0 })}%`;
  };

  const primaryCards = (
    <div className={classNames('kpiOverview__primaryGroup', { 'kpiOverview__primaryGroup--vertical': primaryOnly })}>
      <PrimaryKpiCard
        iconClass="fa-kit fa-baseline"
        iconColor="var(--color-status-pending-600, #2c45d1)"
        iconContainerStyle={{ background: 'linear-gradient(135deg, #dbe3ff 0%, #cbd6ff 100%)' }}
        label={formatMessage({
          defaultMessage: 'Visitor Baseline',
          id: 'driveToStore.footfallReport.kpi.visitorBaseline.label'
        })}
        labelPrimary={primaryOnly}
        value={formatNormalizedCount(data.baseline)}
      >
        <span className="kpiOverview__description">
          {formatMessage({
            defaultMessage: 'Organic visits to the store',
            id: 'driveToStore.footfallReport.kpi.visitorBaseline.description'
          })}
        </span>
      </PrimaryKpiCard>
      <PrimaryKpiCard
        iconClass="fa-kit fa-baseline"
        iconColor="var(--color-status-enabled-600, #0C6262)"
        iconContainerStyle={{ background: 'linear-gradient(135deg, #E2F5F6 0%, #B0F0F3 100%)' }}
        label={formatMessage({
          defaultMessage: 'Attributed Uplift',
          id: 'driveToStore.footfallReport.kpi.attributedUplift.label'
        })}
        labelPrimary={primaryOnly}
        labelTooltip={
          <IconTooltip
            icon="fa-regular fa-circle-info"
            iconColor="var(--color-sonata-primary-500, #7B9FE1) !important"
            place="top"
          >
            {formatMessage({
              defaultMessage: 'Visits measured over the baseline',
              id: 'driveToStore.footfallReport.kpi.attributedUplift.tooltip'
            })}
          </IconTooltip>
        }
        tagConfig={{
          data: data.uplift,
          icon: 'fa-regular fa-arrow-trend-up',
          text: formatMessage(
            {
              defaultMessage: '+{count} Visits',
              id: 'driveToStore.footfallReport.kpi.attributedUplift.vsBaseline'
            },
            { count: formatNumberValue(data.uplift) }
          )
        }}
        value={formatPercent(data.upliftPct, 100, true)}
      >
        <span className="kpiOverview__description">
          {formatMessage({
            defaultMessage: 'Percentage of new visits to store',
            id: 'driveToStore.footfallReport.kpi.attributedUplift.description'
          })}
        </span>
      </PrimaryKpiCard>
      <PrimaryKpiCard
        iconClass="fa-kit fa-dollarfootprint"
        iconColor="var(--color-status-warning-600, #d97706)"
        iconContainerStyle={{ background: 'linear-gradient(135deg, #fde8c8 0%, #fdd5a0 100%)' }}
        iconTooltip={
          <IconTooltip
            icon="fa-regular fa-circle-info"
            iconColor="var(--color-sonata-primary-500, #7B9FE1) !important"
            place="top"
          >
            {formatMessage({
              defaultMessage:
                'This is an estimate. Since one ad can bring customers to several stores, the total spend is distributed.',
              id: 'driveToStore.footfallReport.kpi.cpv.tooltip'
            })}
          </IconTooltip>
        }
        label={formatMessage({
          defaultMessage: 'Average Cost Per Store Visit',
          id: 'driveToStore.footfallReport.kpi.cpv.label'
        })}
        labelPrimary={primaryOnly}
        value={formatMoney(data.costPerVisit, baseCurrency)}
      >
        <span className="kpiOverview__description">
          {formatMessage(
            {
              defaultMessage: 'From {amount} attributed campaign spend',
              id: 'driveToStore.footfallReport.kpi.cpv.description'
            },
            { amount: formatMoney(data.totalSpent, baseCurrency) }
          )}
        </span>
      </PrimaryKpiCard>
    </div>
  );

  const secondaryCards = (
    <div className="kpiOverview__secondaryGroup">
      <div className="kpiOverview__secondaryCards">
        <SecondaryKpiCard
          iconClass="fa-duotone fa-solid fa-cart-shopping"
          label={formatMessage({
            defaultMessage: 'Average Ticket',
            id: 'driveToStore.footfallReport.kpi.averageTicket.label'
          })}
          value={formatCurrencyRange(data.averageTicket)}
        />
        <SecondaryKpiCard
          iconClass="fa-duotone fa-solid fa-filter-circle-dollar"
          label={formatMessage({
            defaultMessage: 'Conversion Rate',
            id: 'driveToStore.footfallReport.kpi.conversionRate.label'
          })}
          value={formatPercentRange(data.conversionRate, 1)}
        />
        <SecondaryKpiCard
          iconClass="fa-duotone fa-solid fa-piggy-bank"
          label={formatMessage({ defaultMessage: 'ROI', id: 'driveToStore.footfallReport.kpi.roi.label' })}
          roiHeader
          value={formatPercent(data.roi, 100)}
        >
          <IconTooltip
            icon="fa-regular fa-circle-info"
            iconColor="var(--color-sonata-primary-500, #7B9FE1) !important"
            place="left"
          >
            <div className="kpiOverview__roiTooltip">
              <span className="kpiOverview__roiTooltipTitle">
                {formatMessage({
                  defaultMessage: 'Return Of Investment.',
                  id: 'driveToStore.footfallReport.kpi.roi.tooltip.part1'
                })}
              </span>
              {formatMessage({
                defaultMessage:
                  'A metric that measures the profitability and efficiency of an investment by comparing the gain or loss generated relative to its initial cost. Calculated by dividing the net profit by the total investment cost and expressing the result as a percentage, a positive ROI indicates that the venture has earned more than it spent.',
                id: 'driveToStore.footfallReport.kpi.roi.tooltip.part2'
              })}
            </div>
          </IconTooltip>
        </SecondaryKpiCard>
      </div>
      <p className="kpiOverview__note">
        {formatMessage({
          defaultMessage: 'This information ranges are derived from consumption pattern models and polls.',
          id: 'driveToStore.footfallReport.kpi.rangeNote'
        })}
      </p>
    </div>
  );

  if (primaryOnly) {
    return <div className="kpiOverview kpiOverview--primaryOnly">{primaryCards}</div>;
  }

  if (secondaryOnly) {
    return <div className="kpiOverview kpiOverview--secondaryOnly">{secondaryCards}</div>;
  }

  return (
    <div className={classNames('kpiOverview', { 'kpiOverview--aggregated': isAggregated })}>
      <div className="kpiOverview__row">
        {primaryCards}
        {!isAggregated && secondaryCards}
      </div>
    </div>
  );
}
